import { useState, type FormEvent } from 'react';
import { useSession } from './session.tsx';
import { Alert, Field, TextInput } from '../components/ui.tsx';
import { Logo } from '../components/Logo.tsx';
import { BrandMotifs } from '../components/BrandMotifs.tsx';
import { ApiError } from '../lib/api.ts';
import { EmployeeOfMonthCard } from './EmployeeOfMonthCard.tsx';

import { IDLE_MINUTES, idleDurationAr } from './useIdleLogout.ts';

export const SESSION_EXPIRED_NOTICE = 'انتهت الجلسة — سجّل الدخول مرة أخرى.';

/**
 * شاشة الدخول — لوحان: نظام الإدارة على اليمين، وتكريم موظف الشهر على اليسار.
 *
 * منطق المصادقة لم يُمسّ: نفس الحقول ونفس التحقق ونفس رسائل الخادم ونفس «نسيت كلمة المرور».
 * ما أُضيف عرضٌ فقط — واللوح الأيسر يختفي كليًا على الجوال فتبقى الشاشة كما كانت تمامًا.
 *
 * والهوية من الشعار الرسمي نفسه: أزرق داكن وسماوي، والشعار يُعرض بملفه الأصلي بلا إعادة
 * تلوين ولا قصّ (مكوّن Logo القائم). وخلفية اللوح الأيسر رسومٌ **مأخوذة من الشعار**
 * (BrandMotifs): طيارة وسحابة ونوارس ونخيل ونجمة بحر — بيضاء بشفافية ٤٪–٩٪ فلا تزعج.
 */
export function LoginPage({ onForgot, expired = false, idle = false }: {
  onForgot?: () => void;
  expired?: boolean;
  /** أُغلقت الجلسة لهجر الشاشة — يُقال السبب كي لا يُظنّ عطلًا */
  idle?: boolean;
} = {}) {
  const { signIn } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try { await signIn(email.trim(), password); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'تعذّر تسجيل الدخول'); }
    finally { setBusy(false); }
  }

  return (
    <main className="grid min-h-screen lg:grid-cols-2">
      {/* لوح الدخول */}
      <div className="flex items-center justify-center p-4 sm:p-8">
        <form onSubmit={onSubmit} className="card w-full max-w-sm space-y-4 p-6" aria-label="تسجيل الدخول">
          <div className="flex flex-col items-center gap-2">
            <Logo priority className="h-24 sm:h-32" />
            <h1 className="text-center text-xl font-bold text-brand-700">iConTravel</h1>
            <p className="text-center text-sm text-ink-500">نظام الإدارة المتكامل</p>
          </div>
          {/**
            * **بلسان المالك** (قرار المالك 22/09/2026: «اتقفلت الجلسة علشان معاليك بعيد
            * عن الجهاز لمدة 10 دقايق») — والرقم من `IDLE_MINUTES` لا مكتوبًا بالحرف،
            * فإن غُيّرت المدّة تغيّرت الجملة معها ولا تكذب على أحد.
            */}
          {idle && !error && (
            <Alert kind="info">
              اتقفلت الجلسة علشان معاليك بعيد عن الجهاز لمدة {idleDurationAr(IDLE_MINUTES)} — سجّل دخولك تاني.
            </Alert>
          )}
          {expired && !idle && !error && <Alert kind="info">{SESSION_EXPIRED_NOTICE}</Alert>}
          {error && <Alert>{error}</Alert>}
          {/**
            * بالبريد أو برقم التليفون (قرار المالك) — والحقل نصّ لا `email` كي يقبل الرقم.
            *
            * **وبلا نصٍّ باهت داخله** (قرار المالك 22/09/2026: «شيل الكتابة الباهتة دي،
            * خلّيها فاضية عادي وهما عارفين هيحطوا إيه»): العنوان فوق الخانة يقول ما
            * يُكتب فيها، والنصُّ الباهت تحته يعيده بمثالٍ يُقرأ لحظةً ثم يزول — وهو في
            * شاشة دخولٍ يفتحها صاحبُها كل يوم زيادةٌ لا إرشاد.
            */}
          <Field label="البريد الإلكتروني أو رقم التليفون" required>
            <TextInput type="text" name="email" autoComplete="username" inputMode="email" dir="ltr" required value={email}
              onChange={e => setEmail(e.target.value)} />
          </Field>
          <Field label="كلمة المرور" required>
            <TextInput type="password" name="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} />
          </Field>
          <button type="submit" className="btn-primary w-full" disabled={busy}>{busy ? 'جارٍ الدخول…' : 'دخول'}</button>
          {onForgot && (
            <button type="button" className="btn-ghost w-full text-sm" onClick={onForgot}>نسيت كلمة المرور؟</button>
          )}
        </form>
      </div>

      {/* لوح التكريم — يظهر على الشاشات الكبيرة وحدها */}
      <aside
        style={{ backgroundImage: 'linear-gradient(155deg, #12939c 0%, #0d7a83 45%, #0a5f68 100%)' }}
        className="relative hidden flex-col items-center justify-center gap-8 overflow-hidden p-10 lg:flex">
{/* رسومٌ من الشعار نفسه: طيارة وسحابة ونوارس ونخيل — بشفافيةٍ تُحسّ ولا تُزعج */}
        <BrandMotifs />

        <div className="relative text-center">
          <h2 className="text-2xl font-bold text-white">معًا نصنع نجاحًا أكبر</h2>
          <p className="mt-2 text-sm text-white/80">فريق واحد · أهداف واضحة · أداء أفضل</p>
        </div>

        <div className="relative flex w-full justify-center">
          <EmployeeOfMonthCard />
        </div>
      </aside>
    </main>
  );
}
